import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  XCircle,
  RefreshCw,
  Loader,
  Activity,
  Square,
  ArrowRight,
  ArrowDown,
  Calendar
} from 'lucide-react';
import { getScraperUrl, SCRAPER_CONFIG } from '../../../constants/scraper';

const RawDataAnalysis = ({ loading, onRefresh }) => {
  const [caseSummary, setCaseSummary] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isScrapingPrefix, setIsScrapingPrefix] = useState({});
  const [isStoppingAll, setIsStoppingAll] = useState(false);
  // isProcessingPrefix state removed - processing now handled by merge service

  const fetchCaseSummary = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setIsLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(
        getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.CASE_SUMMARY)
      );
      const data = await response.json();

      if (data.success) {
        setCaseSummary(data);
      } else {
        setError(data.error || 'Failed to fetch case summary');
      }
    } catch (err) {
      setError('Failed to connect to scraping service');
      console.error('Error fetching case summary:', err);
    } finally {
      if (showLoading) {
        setIsLoading(false);
      }
    }
  }, []);

  // checkPrefixStatus function removed - no longer used

  const handleStopAllScraping = useCallback(async () => {
    setIsStoppingAll(true);
    setError(null);

    try {
      const response = await fetch(
        getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.STOP_ALL),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        // Stop all individual scraping states
        setIsScrapingPrefix({});
        setError(
          `Successfully stopped scraping for ${data.stopped_count} prefixes`
        );

        // Refresh the case summary to get updated data
        await fetchCaseSummary();
      } else {
        setError(data.error || 'Failed to stop scraping');
      }
    } catch (err) {
      setError('Failed to connect to scraping service');
      console.error('Error stopping all scraping:', err);
    } finally {
      setIsStoppingAll(false);
    }
  }, [fetchCaseSummary]);

  // Process file functionality removed - processing now handled by merge service

  const handleStartTailScraping = useCallback(
    async (yearPrefix) => {
      setError(null);
      setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: true }));

      try {
        const response = await fetch(
          getScraperUrl(`/scraping/year-prefix/${yearPrefix}/start-tail`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();

        if (data.success) {
          setError(`Successfully started tail scraping for ${yearPrefix}`);
          // Refresh the case summary to show updated data
          await fetchCaseSummary();
        } else {
          setError(
            data.error || `Failed to start tail scraping for ${yearPrefix}`
          );
        }
      } catch (err) {
        setError(`Failed to connect to scraping service for ${yearPrefix}`);
        console.error('Error starting tail scraping:', err);
      } finally {
        setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: false }));
      }
    },
    [fetchCaseSummary]
  );

  const handleStartFullScraping = useCallback(
    async (yearPrefix) => {
      setError(null);
      setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: true }));

      try {
        const response = await fetch(
          getScraperUrl(`/scraping/year-prefix/${yearPrefix}/start-full`),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();

        if (data.success) {
          setError(`Successfully started full scraping for ${yearPrefix}`);
          // Refresh the case summary to show updated data
          await fetchCaseSummary();
        } else {
          setError(
            data.error || `Failed to start full scraping for ${yearPrefix}`
          );
        }
      } catch (err) {
        setError(`Failed to connect to scraping service for ${yearPrefix}`);
        console.error('Error starting full scraping:', err);
      } finally {
        setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: false }));
      }
    },
    [fetchCaseSummary]
  );

  const handleStartProceedingsScraping = useCallback(
    async (yearPrefix) => {
      setError(null);
      setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: true }));

      try {
        const response = await fetch(
          getScraperUrl(
            `/scraping/year-prefix/${yearPrefix}/start-proceedings`
          ),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();

        if (data.success) {
          setError(
            `Successfully started proceedings scraping for ${yearPrefix}`
          );
          // Refresh the case summary to show updated data
          await fetchCaseSummary();
        } else {
          setError(
            data.error ||
              `Failed to start proceedings scraping for ${yearPrefix}`
          );
        }
      } catch (err) {
        setError(`Failed to connect to scraping service for ${yearPrefix}`);
        console.error('Error starting proceedings scraping:', err);
      } finally {
        setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: false }));
      }
    },
    [fetchCaseSummary]
  );

  const handleStopScraping = useCallback(
    async (yearPrefix) => {
      setError(null);
      setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: true }));

      try {
        const response = await fetch(
          getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.YEAR_PREFIX_CLEAR(yearPrefix)),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            }
          }
        );
        const data = await response.json();

        if (data.success) {
          setError(`Successfully stopped scraping for ${yearPrefix}`);
          // Refresh the case summary to show updated data
          await fetchCaseSummary();
        } else {
          setError(data.error || `Failed to stop scraping for ${yearPrefix}`);
        }
      } catch (err) {
        setError(`Failed to connect to scraping service for ${yearPrefix}`);
        console.error('Error stopping scraping:', err);
      } finally {
        setIsScrapingPrefix((prev) => ({ ...prev, [yearPrefix]: false }));
      }
    },
    [fetchCaseSummary]
  );

  const syncPrefixStatuses = useCallback(async () => {
    try {
      // Get all available prefixes
      const response = await fetch(
        getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.PREFIXES)
      );
      const data = await response.json();

      if (data.success && data.prefixes) {
        // Check status of each prefix
        const statusPromises = data.prefixes.map(async (prefix) => {
          try {
            const statusResponse = await fetch(
              getScraperUrl(SCRAPER_CONFIG.ENDPOINTS.PREFIX_STATUS(prefix))
            );
            const statusData = await statusResponse.json();

            if (statusData.success) {
              return { prefix, status: statusData.status };
            }
          } catch (err) {
            console.error(`Error checking status for ${prefix}:`, err);
          }
          return { prefix, status: 'idle' };
        });

        const statuses = await Promise.all(statusPromises);

        // Update the scraping states based on actual statuses
        const newScrapingStates = {};
        statuses.forEach(({ prefix, status }) => {
          newScrapingStates[prefix] = status === 'running';
        });

        setIsScrapingPrefix(newScrapingStates);
      }
    } catch (err) {
      console.error('Error syncing prefix statuses:', err);
    }
  }, []);

  useEffect(() => {
    fetchCaseSummary();
    syncPrefixStatuses();
  }, [fetchCaseSummary, syncPrefixStatuses]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-3 text-gray-600">Loading case summary...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <XCircle className="mx-auto h-12 w-12 text-red-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Error</h3>
        <p className="mt-1 text-sm text-gray-500">{error}</p>
        <button
          onClick={fetchCaseSummary}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <RefreshCw className="w-4 h-4 mr-2 inline" />
          Retry
        </button>
      </div>
    );
  }

  if (!caseSummary) {
    return (
      <div className="text-center py-12">
        <XCircle className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No Data</h3>
        <p className="mt-1 text-sm text-gray-500">
          No case summary data available
        </p>
      </div>
    );
  }

  const yearGroups = caseSummary.year_groups || {};
  const yearPrefixSummaries = caseSummary.year_prefix_summaries || {};
  const years = Object.keys(yearGroups).sort().reverse(); // Most recent first

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            Redis Scraper Control
          </h2>
          <p className="text-gray-600 mt-1">
            Control scraping jobs and view detailed statistics for each year and
            courthouse prefix
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleStopAllScraping}
            disabled={isStoppingAll || isLoading}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            <Square
              className={`w-4 h-4 mr-2 ${isStoppingAll ? 'animate-pulse' : ''}`}
            />
            {isStoppingAll ? 'Stopping...' : 'Stop All'}
          </button>
          <button
            onClick={fetchCaseSummary}
            disabled={isLoading}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Overall Statistics */}
      <div className="bg-white border rounded-lg p-6 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Overall Statistics
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {caseSummary.metadata?.total_cases?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-gray-600">Total Cases</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-600">
              {caseSummary.summary?.total_completed?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-gray-600">Completed</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-red-600">
              {caseSummary.summary?.total_errors?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-gray-600">Invalid</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-yellow-600">
              {caseSummary.summary?.total_pending?.toLocaleString() || '0'}
            </p>
            <p className="text-sm text-gray-600">Pending</p>
          </div>
        </div>
      </div>

      {/* Year Groups */}
      <div className="space-y-6">
        {years.map((year) => {
          const yearData = yearGroups[year] || {};
          const yearPrefixes = Object.keys(yearData.prefixes || {}).sort();

          return (
            <div key={year} className="bg-white border rounded-lg shadow-sm">
              {/* Year Header */}
              <div className="px-6 py-4 border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Year 20{year}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {yearPrefixes.length} prefixes •{' '}
                      {yearData.total_attempts?.toLocaleString() || '0'} total
                      cases
                    </p>
                  </div>
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="text-center">
                      <div className="text-green-600 font-semibold">
                        {yearData.completed?.toLocaleString() || '0'}
                      </div>
                      <div className="text-gray-500">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-red-600 font-semibold">
                        {yearData.invalid?.toLocaleString() || '0'}
                      </div>
                      <div className="text-gray-500">Invalid</div>
                    </div>
                    <div className="text-center">
                      <div className="text-yellow-600 font-semibold">
                        {yearData.pending?.toLocaleString() || '0'}
                      </div>
                      <div className="text-gray-500">Pending</div>
                    </div>
                    <div className="text-center">
                      <div className="text-green-600 font-semibold">
                        {yearData.success_rate?.toFixed(1) || '0'}%
                      </div>
                      <div className="text-gray-500">Success Rate</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Prefix Grid for this Year */}
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {yearPrefixes.map((yearPrefix) => {
                    const data = yearPrefixSummaries[yearPrefix] || {};
                    const isScraping = isScrapingPrefix[yearPrefix];
                    // isProcessing removed - processing now handled by merge service

                    // Get processed data status for this year+prefix

                    return (
                      <div
                        key={yearPrefix}
                        className="bg-gray-50 border rounded-lg p-4 shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">
                            {yearPrefix}
                          </h4>
                          <div className="flex items-center space-x-2">
                            {isScraping ? (
                              <div className="flex items-center text-blue-600">
                                <Activity className="w-4 h-4 animate-pulse" />
                                <span className="text-xs ml-1">Scraping</span>
                              </div>
                            ) : (
                              <div className="flex items-center text-gray-400">
                                <Square className="w-4 h-4" />
                                <span className="text-xs ml-1">Idle</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Detailed Statistics */}
                        <div className="space-y-2 text-sm text-gray-600 mb-4">
                          <div className="flex justify-between">
                            <span>Total Attempts:</span>
                            <span className="font-medium text-gray-900">
                              {data.total_attempts?.toLocaleString() || '0'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Completed:</span>
                            <span className="font-medium text-green-600">
                              {data.completed?.toLocaleString() || '0'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Invalid:</span>
                            <span className="font-medium text-red-600">
                              {data.invalid?.toLocaleString() || '0'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Pending:</span>
                            <span className="font-medium text-yellow-600">
                              {data.pending?.toLocaleString() || '0'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Timeout:</span>
                            <span className="font-medium text-orange-600">
                              {data.timeout?.toLocaleString() || '0'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Success Rate:</span>
                            <span className="font-medium text-green-600">
                              {data.success_rate?.toFixed(1) || '0'}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Error Rate:</span>
                            <span className="font-medium text-red-600">
                              {data.invalid_rate?.toFixed(1) || '0'}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Pending Rate:</span>
                            <span className="font-medium text-yellow-600">
                              {data.pending_rate?.toFixed(1) || '0'}%
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Last Case:</span>
                            <span className="font-medium text-gray-900">
                              {data.last_case_number || 'N/A'}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span>Last Scraped:</span>
                            <span className="font-medium text-gray-900">
                              {data.last_scraped_at
                                ? new Date(
                                    data.last_scraped_at
                                  ).toLocaleDateString()
                                : 'N/A'}
                            </span>
                          </div>
                        </div>

                        {/* Control Buttons */}
                        <div className="pt-3 border-t space-y-2">
                          {/* Scraping Buttons */}
                          {isScraping ? (
                            <button
                              onClick={() => handleStopScraping(yearPrefix)}
                              className="w-full px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 flex items-center justify-center"
                            >
                              <Square className="w-4 h-4 mr-2" />
                              Stop Scraping
                            </button>
                          ) : (
                            <div className="space-y-2">
                              <button
                                onClick={() =>
                                  handleStartTailScraping(yearPrefix)
                                }
                                className="w-full px-3 py-2 bg-cyan-600 text-white text-sm rounded-md hover:bg-cyan-700 flex items-center justify-center"
                              >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Start Tail Scraping
                              </button>
                              <button
                                onClick={() =>
                                  handleStartFullScraping(yearPrefix)
                                }
                                className="w-full px-3 py-2 bg-orange-600 text-white text-sm rounded-md hover:bg-orange-700 flex items-center justify-center"
                              >
                                <ArrowDown className="w-4 h-4 mr-2" />
                                Start Full Scraping
                              </button>
                              <button
                                onClick={() =>
                                  handleStartProceedingsScraping(yearPrefix)
                                }
                                className="w-full px-3 py-2 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 flex items-center justify-center"
                              >
                                <Calendar className="w-4 h-4 mr-2" />
                                Start Proceedings Scraping
                              </button>
                            </div>
                          )}

                          {/* Processing removed - now handled by merge service */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

RawDataAnalysis.propTypes = {
  loading: PropTypes.bool,
  onRefresh: PropTypes.func
};

export default RawDataAnalysis;
